import {
  SoulMapProfileForm,
  validateSoulMapProfileForm,
  writeSoulMapProfileDraft,
} from '@/utils/soulmap-profile';
import { useEffect, useState } from 'react';
import { scrollToFirstSoulMapProfileError } from './scroll-to-first-profile-error';
import {
  SoulMapProfileFieldErrors,
  SoulMapProfileFields,
} from './soul-map-profile-fields';

interface SoulMapProfileFormProps {
  initialForm: SoulMapProfileForm;
  isLoading: boolean;
  isSubmitting: boolean;
  isLoadingAgents: boolean;
  agentReady: boolean;
  onSubmit: (form: SoulMapProfileForm) => void;
}

export function SoulMapProfileFormView({
  initialForm,
  isLoading,
  isSubmitting,
  isLoadingAgents,
  agentReady,
  onSubmit,
}: SoulMapProfileFormProps) {
  const [form, setForm] = useState<SoulMapProfileForm>(initialForm);
  const [errors, setErrors] = useState<SoulMapProfileFieldErrors>({});

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const isBusy = isLoading || isSubmitting || isLoadingAgents;

  const updateForm = (patch: Partial<SoulMapProfileForm>) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      writeSoulMapProfileDraft(next);
      return next;
    });
  };

  const clearError = (field: keyof SoulMapProfileFieldErrors) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = () => {
    const validationErrors = validateSoulMapProfileForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstSoulMapProfileError(validationErrors);
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="sm-fade-up mx-auto flex w-full flex-1 flex-col px-6 py-16 text-left">
      <div className="mx-auto w-full max-w-[480px] text-center">
        <p className="sm-eyebrow mb-6">Before we begin</p>
        <h1
          className="sm-serif mb-4"
          style={{
            fontSize: 'clamp(24px, 4vw, 32px)',
            fontWeight: 500,
            lineHeight: 1.25,
          }}
        >
          Tell us a little about you
        </h1>
        <p
          className="mb-10"
          style={{
            fontSize: 14.5,
            color: 'var(--sm-muted)',
            lineHeight: 1.6,
          }}
        >
          These details shape how Soul Map speaks with you and prepare your
          chart from the first exchange. You can update them anytime.
        </p>
      </div>

      <div className="mx-auto w-full max-w-[480px] space-y-6">
        <SoulMapProfileFields
          form={form}
          errors={errors}
          disabled={isBusy}
          onChange={updateForm}
          onClearError={clearError}
        />

        <div className="pt-2 text-center">
          <button
            type="button"
            className="sm-btn-primary"
            onClick={handleSubmit}
            disabled={isBusy || !agentReady}
          >
            {isSubmitting
              ? 'Starting…'
              : isLoadingAgents
                ? 'Loading…'
                : 'Begin Soul Map'}
          </button>
          {!isLoadingAgents && !agentReady && (
            <p className="mt-4 text-sm" style={{ color: 'var(--sm-coral)' }}>
              Soul Map isn’t available just yet. Please try again in a few minutes.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
